import type { QueryClient } from '@tanstack/react-query'

import { patchFromProjectRead } from '@/shared/api/adapt'
import { api } from '@/shared/api/client'
import type { ProjectRead } from '@/shared/api/contracts'
import { qk } from '@/shared/api/query-keys'
import { useReorderMutation } from '@/shared/hooks/use-reorder-mutation'
import { useT } from '@/shared/i18n'
import { moveByNeighbors, moveNextTo, type Anchor } from '@/shared/lib/rank'
import type { ProjectSummary, Stage } from '@/shared/types/domain'

import { useProjectStages } from './api'

/**
 * Mover un proyecto: `/priorities` (solo orden) y `/board` (orden + fase).
 *
 * Las dos pantallas comparten esta mutación a propósito. El rank del proyecto es
 * uno solo — global cross-company, particionado por `is_ongoing` (plan 2.3) — así
 * que arrastrar en el tablero tiene que mover la lista priorizada y al revés.
 * Con dos mutaciones distintas eso dura hasta el primer parche.
 *
 * El contrato con el backend (`POST /projects/{id}/move` con vecinos, el rank lo
 * calcula el server) está documentado en `shared/hooks/use-reorder-mutation.ts`.
 */
export interface MoveProjectVariables {
  id: string
  /** El proyecto que queda ARRIBA. `null` = primer puesto. */
  beforeId: string | null
  /** El proyecto que queda ABAJO. `null` = último puesto. */
  afterId: string | null
  /** Solo en el tablero, cuando el drop cambió de columna. */
  stage?: Stage
  /**
   * Pegado a un proyecto en vez de entre dos vecinos: lo que manda una vista
   * filtrada (ver `anchorForDrop`). Con ancla, `beforeId`/`afterId` no viajan.
   */
  anchor?: Anchor
}

/**
 * Mismo orden que devuelve el backend: ongoing al final, después por posición.
 *
 * Antes comparaba `priority_rank` por bytes. El listado **no devuelve el
 * rank**: el servidor ordena con él y expone el `ROW_NUMBER()` resultante como
 * `position`, que es lo que llega y lo que la pantalla muestra. Los ongoing no
 * tienen ordinal (`position: null`) y van al final, que es donde el backend
 * también los pone.
 *
 * Se aplica **al llegar del API**, no en cada render: una vez ordenada, la
 * cache ES el orden, y por eso el reordenamiento optimista del arreglo se ve
 * aunque las posiciones todavía sean las viejas.
 */
export function sortProjects(projects: readonly ProjectSummary[]): ProjectSummary[] {
  return [...projects].sort((a, b) => {
    if (a.is_ongoing !== b.is_ongoing) return a.is_ongoing ? 1 : -1
    return (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER)
  })
}

function isProjectList(value: unknown): value is ProjectSummary[] {
  return (
    Array.isArray(value) &&
    (value.length === 0 ||
      (typeof value[0] === 'object' &&
        value[0] !== null &&
        'stage' in value[0] &&
        'company' in value[0]))
  )
}

/** Recorre todas las listas de proyectos cacheadas y las reescribe. */
export function patchProjectLists(
  client: QueryClient,
  id: string,
  update: (projects: ProjectSummary[]) => ProjectSummary[],
): void {
  client.setQueriesData({ queryKey: qk.portfolio.all }, (old: unknown) => {
    if (!isProjectList(old)) return old
    if (!old.some((project) => project.id === id)) return old
    return update(old)
  })
}

/**
 * El update optimista toca TODAS las listas de proyectos cacheadas bajo
 * `portfolio`, no solo la pantalla donde ocurrió el drop: si el tablero y las
 * prioridades no se mueven juntos, volver a la otra pestaña muestra el orden
 * viejo hasta que refetchee, y eso es exactamente "las dos vistas se
 * contradicen".
 *
 * Mueve el ítem de índice, sin tocar `priority_rank`: ese lo trae el server.
 */
function applyProjectMove(
  client: QueryClient,
  { id, beforeId, afterId, stage, anchor }: MoveProjectVariables,
): void {
  patchProjectLists(client, id, (projects) =>
    (anchor
      ? moveNextTo(projects, id, anchor)
      : moveByNeighbors(projects, id, beforeId, afterId)
    ).map((project) => (project.id === id && stage ? { ...project, stage } : project)),
  )
}

/**
 * Llega la respuesta: la posición autoritativa entra en su lugar, sin reordenar.
 *
 * Se parchea, no se reemplaza: `ProjectRead` no trae el nombre de la empresa ni
 * su hue, así que pisar la fila entera dejaría la espina de color en blanco
 * justo después de arrastrar.
 */
function reconcileProject(client: QueryClient, moved: ProjectRead, stages: readonly Stage[]): void {
  if (!moved?.id) return

  const patch = patchFromProjectRead(moved, stages)
  patchProjectLists(client, moved.id, (projects) =>
    projects.map((project) => (project.id === moved.id ? { ...project, ...patch } : project)),
  )
}

export function useMoveProject() {
  const t = useT()
  const stages = useProjectStages()

  return useReorderMutation<MoveProjectVariables, ProjectRead>({
    queryKeys: [qk.portfolio.all],
    invalidateOnSettled: [qk.projects.all],
    apply: applyProjectMove,
    reconcile: (client, data) => reconcileProject(client, data, stages.data ?? []),
    mutationFn: ({ id, beforeId, afterId, stage, anchor }) =>
      api.post<ProjectRead>(
        `projects/${id}/move`,
        anchor
          ? { anchor_id: anchor.id, placement: anchor.placement }
          : {
              before_id: beforeId,
              after_id: afterId,
              ...(stage ? { stage_id: stage.id } : {}),
            },
      ),
    errorText: t('projects.error.reorder'),
    entity: 'project',
  })
}
